import { useQuery } from '@tanstack/react-query'
import moment from 'moment-timezone'
import { apiCall } from '../../../utils/restclient'
import { buildAvailabilityEndpoint } from '../../../store/new-visit/NewVisitThunk'
import { TIME_FOR_DATE_RANGE_REQUEST_FROM, TIME_FOR_DATE_RANGE_REQUEST_TO } from '../../../utils/dateUtils'

type AvailabilityTimeSlot = { Date: string; Times: string[] }
type AvailabilityResult = {
  Provider: { Id: string; [key: string]: unknown }
  TimeSlotCollection: AvailabilityTimeSlot[]
  HasPastVisit: boolean
}

type Params = {
  providerId?: string | null
  date: string
  timeZone: string
  visitTypeId?: string | null
  patientId?: string | null
}

export const useProviderAvailability = ({ providerId, date, timeZone, visitTypeId, patientId }: Params) => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['provider-availability', providerId, date, timeZone, visitTypeId, patientId],
    queryFn: async () => {
      const parsed = moment(date, 'dddd MMM D YYYY')
      const fromDateTime = parsed.format(TIME_FOR_DATE_RANGE_REQUEST_FROM)
      const toDateTime = parsed.format(TIME_FOR_DATE_RANGE_REQUEST_TO)

      const endpoint = buildAvailabilityEndpoint({
        fromDateTime,
        toDateTime,
        timeZone: timeZone ?? '',
        visitTypeId: visitTypeId ?? '',
        patientId: patientId ?? '',
        providerId,
      })

      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- apiCall returns unknown; shape is the API contract
      return apiCall(endpoint, 'GET') as Promise<AvailabilityResult[]>
    },
    enabled: Boolean(providerId && date && timeZone && visitTypeId),
    select: (results: AvailabilityResult[]) => results[0]?.TimeSlotCollection[0]?.Times ?? [],
  })

  return {
    timeSlots: data ?? [],
    isLoading,
    isError,
  }
}
